// <s-marquee> — seamless continuous scroll of its [data-marquee-track] content.
// Clones the original items enough times to cover the viewport, measures the exact
// distance of one repeat (including the gap) and animates with the Web Animations API.
// Re-measures after fonts load and on resize so the loop never jumps.
// Respects prefers-reduced-motion (no animation) and pauses on hover. One component per file.
class SMarquee extends HTMLElement {
  #animation = null;
  #ac = null;
  #mql = null;
  #originals = null;
  #observer = null;
  #lastWidth = 0;
  #frame = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    this.#mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.#mql.addEventListener("change", () => this.#schedule(), { signal: this.#ac.signal });

    this.addEventListener("mouseenter", () => this.#animation?.pause(), { signal: this.#ac.signal });
    this.addEventListener("mouseleave", () => this.#animation?.play(), { signal: this.#ac.signal });

    const track = this.querySelector("[data-marquee-track]");
    if (track) {
      this.#originals = [...track.children].filter((n) => !n.hasAttribute("data-marquee-clone"));
    }

    // Widths change once web fonts arrive; measure again so the repeat distance is exact.
    document.fonts?.ready?.then(() => this.#schedule());

    this.#observer = new ResizeObserver(() => {
      if (this.clientWidth !== this.#lastWidth) this.#schedule();
    });
    this.#observer.observe(this);

    this.#schedule();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
    this.#animation?.cancel();
  }

  #schedule() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#setup());
  }

  #setup() {
    const track = this.querySelector("[data-marquee-track]");
    if (!track || !this.#originals?.length) return;

    // Keep the current position so a re-measure doesn't visibly restart the strip.
    const progress = this.#animation?.effect?.getComputedTiming()?.progress ?? 0;
    this.#animation?.cancel();
    this.#animation = null;
    this.#lastWidth = this.clientWidth;

    track.querySelectorAll("[data-marquee-clone]").forEach((n) => n.remove());
    if (this.#mql.matches) return;

    const first = this.#originals[0];
    const last = this.#originals[this.#originals.length - 1];
    const setWidth = last.offsetLeft + last.offsetWidth - first.offsetLeft;
    if (setWidth <= 0) return;

    // Enough copies that the strip is always full, plus one spare for the wrap.
    const copies = Math.max(1, Math.ceil(this.clientWidth / setWidth)) + 1;
    for (let i = 0; i < copies; i++) {
      for (const node of this.#originals) {
        const clone = node.cloneNode(true);
        clone.setAttribute("data-marquee-clone", "");
        clone.setAttribute("aria-hidden", "true");
        track.append(clone);
      }
    }

    // Exact distance from one repeat to the next, gap included.
    const firstClone = track.querySelector("[data-marquee-clone]");
    const distance = firstClone.offsetLeft - first.offsetLeft;
    if (distance <= 0) return;

    const speed = parseFloat(this.dataset.speed) || 40;
    const durationMs = (distance / speed) * 1000;
    const reverse = this.dataset.direction === "right";
    const from = reverse ? -distance : 0;
    const to = reverse ? 0 : -distance;

    this.#animation = track.animate(
      [{ transform: `translateX(${from}px)` }, { transform: `translateX(${to}px)` }],
      { duration: durationMs, iterations: Infinity, easing: "linear" },
    );
    if (progress) this.#animation.currentTime = progress * durationMs;
  }
}

if (!customElements.get("s-marquee")) {
  customElements.define("s-marquee", SMarquee);
}
